import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { BRAND, GRAY, ACCENT } from "../colors";

const REPLY_PARTS = [
  { text: "Hi Sarah! Thanks for choosing ", bold: false },
  { text: "Joe's Pizza", bold: true },
  { text: ". We're thrilled you loved our famous ", bold: false },
  { text: "Brooklyn-style crust", bold: true },
  { text: "! Our team takes pride in every pie we make at our ", bold: false },
  { text: "Park Slope location", bold: true },
  { text: ". See you next time!", bold: false },
];

const FULL_TEXT = REPLY_PARTS.map((p) => p.text).join("");

export const SeoReplyAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const reviewAppear = spring({ frame, fps, config: { damping: 14 } });
  const sparkle = spring({ frame: frame - 30, fps, config: { damping: 12 } });
  const replyAppear = spring({ frame: frame - 40, fps, config: { damping: 14 } });

  const typingProgress = interpolate(frame, [45, 120], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const typedLength = Math.floor(typingProgress * FULL_TEXT.length);
  const showCursor = frame > 45 && frame < 125;

  // Compute visible parts with bold styling
  const visibleParts: { text: string; bold: boolean }[] = [];
  let remaining = typedLength;
  for (const part of REPLY_PARTS) {
    if (remaining <= 0) break;
    const chars = Math.min(remaining, part.text.length);
    visibleParts.push({ text: part.text.slice(0, chars), bold: part.bold });
    remaining -= chars;
  }

  const seoTagsAppear = spring({ frame: frame - 125, fps, config: { damping: 12 } });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: GRAY[50],
        padding: 32,
        justifyContent: "center",
        gap: 14,
      }}
    >
      {/* Review */}
      <div
        style={{
          transform: `translateY(${interpolate(reviewAppear, [0, 1], [30, 0])}px)`,
          opacity: reviewAppear,
          backgroundColor: "white",
          borderRadius: 14,
          padding: "16px 22px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          border: `1px solid ${GRAY[200]}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: "#FDE68A",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 15,
              fontWeight: 700,
              color: "#92400E",
            }}
          >
            SJ
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 600, color: GRAY[800] }}>Sarah J.</div>
            <div style={{ fontSize: 15, color: "#F59E0B", letterSpacing: 2 }}>★★★★★</div>
          </div>
        </div>
        <div style={{ fontSize: 16, color: GRAY[600], lineHeight: 1.5 }}>
          &quot;Best pizza in Brooklyn! The crust is perfect and the staff is so friendly!&quot;
        </div>
      </div>

      {/* AI sparkle */}
      <div
        style={{
          transform: `scale(${sparkle})`,
          opacity: sparkle,
          display: "flex",
          alignItems: "center",
          gap: 8,
          alignSelf: "center",
        }}
      >
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${BRAND[400]}, ${BRAND[600]})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ fontSize: 13, color: "white" }}>✦</span>
        </div>
        <span style={{ fontSize: 15, fontWeight: 600, color: BRAND[600] }}>
          Generating personalized reply...
        </span>
      </div>

      {/* Reply with highlighted keywords */}
      <div
        style={{
          opacity: replyAppear,
          backgroundColor: "white",
          borderRadius: 14,
          padding: "16px 22px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          border: `1px solid ${ACCENT[400]}`,
          borderLeftWidth: 4,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, color: ACCENT[600], marginBottom: 8 }}>
          SEO-Optimized Reply
        </div>
        <div style={{ fontSize: 16, color: GRAY[700], lineHeight: 1.6 }}>
          {visibleParts.map((part, i) => (
            <span
              key={i}
              style={{
                fontWeight: part.bold ? 700 : 400,
                color: part.bold ? BRAND[700] : GRAY[700],
                backgroundColor: part.bold ? BRAND[50] : "transparent",
                borderRadius: part.bold ? 3 : 0,
                padding: part.bold ? "0 2px" : 0,
              }}
            >
              {part.text}
            </span>
          ))}
          {showCursor && (
            <span
              style={{
                display: "inline-block",
                width: 2,
                height: 17,
                backgroundColor: BRAND[500],
                marginLeft: 1,
                opacity: Math.sin(frame * 0.3) > 0 ? 1 : 0,
              }}
            />
          )}
        </div>
      </div>

      {/* SEO keyword tags */}
      {frame >= 125 && (
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "center",
            flexWrap: "wrap",
            opacity: seoTagsAppear,
            transform: `translateY(${interpolate(seoTagsAppear, [0, 1], [10, 0])}px)`,
          }}
        >
          {["Business Name ✓", "Location ✓", "Keywords ✓", "Personalized ✓"].map((tag) => (
            <div
              key={tag}
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: ACCENT[600],
                backgroundColor: ACCENT[100],
                padding: "4px 10px",
                borderRadius: 10,
              }}
            >
              {tag}
            </div>
          ))}
        </div>
      )}
    </AbsoluteFill>
  );
};
